package com.wintermar.inspection.feature.presentation.inspection.landing

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import com.wintermar.inspection.core.ui.component.TextBodyRegular
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.modal.ConfirmationDialog
import com.wintermar.inspection.core.ui.component.modal.util.ModalAction
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import com.wintermar.inspection.core.ui.util.ScrollDirection
import com.wintermar.inspection.core.ui.util.ScrollDirectionConfig
import com.wintermar.inspection.core.ui.widget.EmptyContent
import com.wintermar.inspection.core.ui.widget.LoadingDialog
import com.wintermar.inspection.core.ui.widget.PullToRefreshContainer
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.presentation.inspection.landing.widget.InspectionLandingMoreActionContent
import com.wintermar.inspection.feature.presentation.inspection.landing.widget.InspectionLandingMoreActionModal
import com.wintermar.inspection.feature.presentation.inspection.landing.widget.choose_template.ChooseTemplateModal
import com.wintermar.inspection.feature.presentation.inspection.landing.widget.inspection_preview.InspectionPreviewModal
import com.wintermar.inspection.feature.util.expect_init.LandingHeader
import com.wintermar.inspection.feature.util.expect_init.setupLandingContent
import com.wintermar.inspection.graphql.type.InspectionStatus
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.are_you_sure_want_to_delete_the_inspection
import inspection.composeapp.generated.resources.auditor
import inspection.composeapp.generated.resources.cancel
import inspection.composeapp.generated.resources.continue_inspection
import inspection.composeapp.generated.resources.create_inspection
import inspection.composeapp.generated.resources.delete
import inspection.composeapp.generated.resources.delete_inspection
import inspection.composeapp.generated.resources.edit
import inspection.composeapp.generated.resources.get_inspection
import inspection.composeapp.generated.resources.inspection_progress
import inspection.composeapp.generated.resources.no_inspetions_scheduled
import inspection.composeapp.generated.resources.view_report
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun InspectionLandingScreen(
    paddingValues: PaddingValues,
    updatedProgressInspection: InspectionModel,
    showSuccessUpdateToast: Boolean,
    excludeInspectionId: String,
    viewModel: InspectionLandingViewModel = koinViewModel(),
    onGoToInspection: (inspectionId: String) -> Unit,
    onCompleted: (data: InspectionModel, templateOptions: List<TemplateOptionModel>) -> Unit
) {
    val state = viewModel.state
    val color: ThemeColor = LocalAppTheme.current.color
    val dimension: AppDimension = LocalAppTheme.current.dimension
    val listState = rememberLazyListState()
    var showChooseTemplate by remember { mutableStateOf(false) }
    var topPadding by remember { mutableStateOf(dimension.spacing.space0) }
    var widthActionButtonBasedOnText by remember { mutableStateOf(false) }

    LaunchedEffect(updatedProgressInspection) {
        if (updatedProgressInspection.id.isEmpty()) return@LaunchedEffect

        state.updateProgress(updatedProgressInspection, showSuccessUpdateToast)
    }

    LaunchedEffect(Unit) {
        setupLandingContent(dimension) { topPaddingContent, widthButtonBasedOnText ->
            widthActionButtonBasedOnText = widthButtonBasedOnText
            topPadding = topPaddingContent
        }

        viewModel.navigationEvent.collect { event ->
            when (event) {
                is InspectionLandingEvent.NavigateToInspectionAnswer -> onGoToInspection(
                    event.inspectionId
                )
            }
        }
    }

    LaunchedEffect(excludeInspectionId) {
        if (excludeInspectionId.isEmpty()) return@LaunchedEffect

        state.onExcludeInspectionId(excludeInspectionId)
    }

    ScrollDirection(
        listState = listState,
        config = ScrollDirectionConfig(
            endReachedOffset = 2,
            endReachedEnabled = state.hasMoreData && !state.isLoadingLoadMore,
        ),
        onEndReached = {
            state.loadMore()
        }
    )

    PullToRefreshContainer(
        isRefreshing = state.isRefreshing,
        onRefresh = { state.onRefresh() },
        modifier = Modifier
            .fillMaxSize()
            .padding(
                top = paddingValues.calculateTopPadding(),
            )
    ) {
        Column {
            LandingHeader(stringResource(Res.string.auditor))
            HeaderContent {
                showChooseTemplate = true
            }

            if (state.data.data.isNotEmpty()) {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize()
                        .padding(
                            top = topPadding,
                            bottom = paddingValues.calculateBottomPadding()
                        )
                        .background(color.bg.normal)
                ) {
                    itemContent(
                        data = state.data.data,
                        onClick = { state.onItemClick(it) },
                        onMoreClick = { state.onShowActionModal(true, it) }
                    )

                    if (state.isLoadingLoadMore) {
                        item {
                            Column(
                                modifier = Modifier.fillMaxWidth().padding(
                                    dimension.spacing.space3
                                ),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(dimension.size.icon.s)
                                )
                            }

                        }
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(bottom = paddingValues.calculateBottomPadding())
                ) {
                    item {
                        EmptyContent {
                            TextBodyRegular(
                                text = stringResource(Res.string.no_inspetions_scheduled),
                                bold = true,
                                textAlign = TextAlign.Center
                            )
                            TextBodySmall(
                                text = stringResource(Res.string.inspection_progress),
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
            }
        }

        BottomNewInspection(
            paddingValues = paddingValues,
            dimension = dimension,
            onClick = {
                showChooseTemplate = true
            }
        )

        ChooseTemplateModal(
            show = showChooseTemplate,
            templates = state.templates,
            onStartInspection = {
                showChooseTemplate = false
                state.onStartInspection(it.id, it.value)
            },
            onDismiss = {
                showChooseTemplate = false
            }
        )

        InspectionLandingMoreActionModal(
            show = state.showActionModal,
            onDismiss = {
                state.onShowActionModal(false)
            }
        ) {
            InspectionLandingMoreActionContent(
                data = state.actionData,
                onDelete = {
                    state.onShowActionModal(false, state.actionData)
                    state.onShowActionConfirmationModal(true, state.actionData)
                }
            )
        }

        InspectionPreviewModal(
            show = state.showInspectionDetailModal,
            data = state.actionData ?: InspectionModel(),
            templateOptions = state.templateOptions,
            labelAction = stringResource(
                if (state.actionData?.status == InspectionStatus.COMPLETED)
                    Res.string.view_report else Res.string.continue_inspection
            ),
            labelSecondAction = if (state.actionData?.status == InspectionStatus.COMPLETED) stringResource(
                Res.string.edit
            ) else "",
            onAction = {
                val data = state.actionData
                state.onShowInspectionDetailModal(false)
                if (data?.status == InspectionStatus.IN_PROGRESS) {
                    onGoToInspection(data.id)
                    return@InspectionPreviewModal
                }
                data?.let {
                    onCompleted(it, state.templateOptions)
                }
            },
            onSecondAction = {
                val id = state.actionData?.id ?: ""
                state.onShowInspectionDetailModal(false)
                state.onEditWithStatusComplete(id)
            },
            onDismiss = {
                state.onShowInspectionDetailModal(false)
            }
        )

        ConfirmationDialog(
            show = state.showActionConfirmationModal,
            title = stringResource(Res.string.delete_inspection),
            message = stringResource(
                Res.string.are_you_sure_want_to_delete_the_inspection,
                state.actionData?.templateName ?: "",
                if (state.actionData?.vesselName?.isNotEmpty() == true) " for ${state.actionData?.vesselName}" else ""
            ),
            action = ModalAction.default().copy(
                showAction = true,
                isDangerAction = true,
                withBackgroundHeader = true,
                widthButtonBasedOnText = widthActionButtonBasedOnText,
                isSameWeight = true,
                label = stringResource(Res.string.delete),
                secondLabel = stringResource(Res.string.cancel),
                onClick = {
                    state.onDelete(state.actionData?.id ?: "")
                    state.onShowActionConfirmationModal(false)
                },
                secondOnClick = {
                    state.onShowActionConfirmationModal(false)
                }),
            onDismiss = {
                state.onShowActionConfirmationModal(false)
            },
        )

        LoadingDialog(
            isLoading = state.isLoadingStartInspection,
            message = stringResource(Res.string.create_inspection)
        )

        LoadingDialog(
            isLoading = state.isLoadingInspectionDetail,
            message = stringResource(Res.string.get_inspection)
        )

        LoadingDialog(isLoading = state.isLoading)
    }
}

expect fun LazyListScope.itemContent(
    data: List<InspectionModel>,
    onClick: (inspectionId: String) -> Unit,
    onMoreClick: (data: InspectionModel) -> Unit
)

@Composable
expect fun BottomNewInspection(
    paddingValues: PaddingValues,
    dimension: AppDimension,
    onClick: () -> Unit
)

@Composable
expect fun HeaderContent(
    onClick: () -> Unit
)
